const request = require('superagent');
const baseURL = __CONFIG__.api;
import { jwtDecode } from "jwt-decode";
import { NotificationManager } from "react-notifications";

let isRedirecting = false;

function handleSessionExpired() {
    if (isRedirecting) return;
    isRedirecting = true;

    const nodes = document.querySelectorAll('.notification-container');
    nodes.forEach(n => n.style.display = 'none');

    localStorage.clear();
    sessionStorage.clear();

    localStorage.setItem('session_expired_flash', 'true');

    window.location.hash = "/login";

    // Mantener el bloqueo por 5 segundos hasta que el Login esté estable
    setTimeout(() => { isRedirecting = false; }, 5000);
}

/**
 * Funcion para obtener el token
 * @return {string|false} - Bearer token o false si no existe
 * */
async function getToken() {
    let token = localStorage.getItem("token");
    const currentTime = Date.now() / 1000;
    const bufferTime = 60 * 60; // 1 hora

    if (token) {
        try {
        const decoded = jwtDecode(token);

        // El token sigue siendo válido y falta más de 1 hora
        if (decoded.exp - bufferTime > currentTime) {
            return `Bearer ${token}`;
        }

        // Está cerca de expirar o ya expiró
        const newToken = await fetchToken();
        if (newToken) {
            return `Bearer ${newToken}`;
        }

        handleSessionExpired();
        return false;
        } catch (error) {
            handleSessionExpired();
            return false;
        }
    }

    // No hay token en storage, intentamos usar el 'refresh' si existe
    const refreshToken = localStorage.getItem("refresh");
    if (refreshToken) {
        const newToken = await fetchToken();
        if (newToken) return `Bearer ${newToken}`;

        handleSessionExpired();
        return false;
    }
    handleSessionExpired(); // Finalizar
    return false;
}

/**
 * Funcion para hacer la url absoluta con query string a partir de una Url relativa y params
 * @param path: string: ruta relativa
 * @param params: dict: parametros para poner como query string
 * @return: string: string con la url absoluta para la peticion
 * */
export function makeUrl(path, params = {}) {
    let url = '/api';
    if (path[0] === '/') {
        url += `${path}/`;
        let url2 = url.slice(0, -1);
        url = url2
    } else {
        url += `/${path}/`;
    }
    
    let hasQueue = false; // se coloca que no hay aun query string
    // por cada atributo en los params se inserta en el query string
    const dicKeys = Object.keys(params);
    dicKeys.forEach((row) => {
        if (hasQueue) {
            url += `&${row}=${params[row]}`;
        } else {
            url += `?${row}=${params[row]}`;
            hasQueue = true;
        }
    });
   
    // Se retorna la url absoluta con query string si fuera el caso
    return baseURL + url;
}

/*
  * Buscar el token vigente y actualizar si ya venció
* */
let refreshInProgress = false;
let refreshPromise;

async function fetchToken() {
    const refresh = localStorage.getItem("refresh");
    if (!refresh) {
        return null;
    }

    // Verificar si el refresh token está expirado
    const decodedRefresh = jwtDecode(refresh);
    if (decodedRefresh.exp < Date.now() / 1000) {
        handleSessionExpired();
        return null;
    }

    if (!refreshInProgress) {
        refreshInProgress = true;
        refreshPromise = (async () => {
        try {
            let response = await fetch(`${baseURL}/api/token/refresh/`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ refresh: refresh }),
            });
            if (!response.ok) {
            throw new Error(`HTTP error status: ${response.status}`);
            }
            const data = await response.json();
            localStorage.setItem("token", data.access);
            if (data.refresh) {
            localStorage.setItem("refresh", data.refresh);
            }
            return data.access;
        } catch (error) {
            handleSessionExpired();
            return null;
        } finally {
            refreshInProgress = false;
        }
        })();
    }
    return refreshPromise;
}

/**
 * Función para manejar los errores de cualquier petición con soporte JWT
 * @param response: response: response de la peticion
 * @param originalRequest: function: función original para reintentar
 * @return {Promise} - Promise del retry o redirección
 */
function errorHandler(response) {
    if (isRedirecting) return;

    if (!response) {
        NotificationManager.error("Error de conexión del servidor", "Error", 9000);
        return;
    }
    
    const status = response.statusCode || response.status;
    const body = response.body || {};
    const message = body.msj || body.message || "Ha ocurrido un error inesperado";

    // 401: No autorizado (Token muerto o inexistente)
    if (status === 401 || status === 204 || isRedirecting) {
        handleSessionExpired();
        return;
    }
    
    // 403: Prohibido (Tiene token, pero no tiene permiso para este recurso)
    if (status === 403) {
        const errorMessage =
            message || "No tiene permisos para realizar esta acción";
        
        NotificationManager.error(
            errorMessage, 
            "Error", 
            9000
        );
        return;
    }

    // 405: Manejar permisos del API (read, create, update, delete)
    if (status === 405) {
        const errorMessage =
            response.body.msj ||
            response.body.message ||
            "Acceso denegado por permisos";
        NotificationManager.error(
            errorMessage, 
            "Error", 
            9000
        );

        // Si hay historial regresamos, si no, lo mandamos al home o dashboard
        setTimeout(() => {
        if (window.history.length > 1) {
            window.history.back();
        } else {
            window.location.assign("/#/");
        }
        }, 2000);
        return;
    }

    NotificationManager.error(
        message, 
        "Error", 
        9000
    );
}

/**
 * Funcion para hacer una peticion post
 * @param path: string: path relativo de la peticion
 * @param body: dict: el body para el post
 * @param params: dict: parametros para query string, opcionales
 * @return: instancia de superagent lista para ser recibida como promise
 * */
async function _post(path, body, params = {}) {
    const url = makeUrl(path, params);
    const token = await getToken();

    if (!token) {
        throw new Error("No valid token available");
    }

    return request
        .post(url)
        .send(body)
        .set("Accept", "application/json")
        .set("Content-Type", "application/json")
        .set("Authorization", token); // Ya es seguro usarlo
}

/**
 * Funcion para hacer una peticion post
 * @param path: string: path relativo de la peticion
 * @param body: dict: el body para el post
 * @param params: dict: parametros para query string, opcionales
 * @return: Promise: promise del post
 * */
async function post(path, body, params = {}) {
    try {
        const response = await _post(path, body, params);
        return response.body || response;
    } catch (error) {
        if (error.response) {
            // Solo llamar al errorHandler si NO es un error de validación (400)
            // para evitar que salte un toast genérico cuando el error es de un campo.
            if (error.response.status !== 400) {
                errorHandler(error.response);
            }

            throw error.response.body || error;
        }
        // Errores de red o de código interno
        throw error;
    }
}


/**
 * Funcion para hacer una peticion post
 * @param path: string: path relativo de la peticion
 * @param body: dict: el body para el post
 * @param attachments: array: diccionario con estructura name, file, con el nombre y archivo que se desea enviar sdf sdf
 * @param params: dict: parametros para query string, opcionales
 * @return: instancia de superagent lista para ser recibida como promise
 * */
async function _postMultiPart(path, body, attachments, params = {}) {
    const url = makeUrl(path, params);
    const token = await getToken();
    if (!token) {
        throw new Error("No valid token available");
    }
    let result = request.post(url).set("Authorization", token);
    attachments &&
        attachments.forEach((attachment) => {
        result.attach(attachment.name, attachment.file);
        });
    const data = JSON.stringify(body);
    result.field("data", data);
    return result;
}

/**
 * Funcion para hacer una peticion post
 * @param path: string: path relativo de la peticion
 * @param body: dict: el body para el post
 * @param attachments: array: diccionario con estructura name, file, con el nombre y archivo que se desea enviar
 * @param params: dict: parametros para query string, opcionales
 * @return: Promise: promise del post
 * */
async function postAttachments(path, body, attachments, params = {}) {
    try {
        const response = await _postMultiPart(path, body, attachments, params);
        return response.body || response;
    } catch (error) {
        if (error.response) {
            errorHandler(error.response);
            throw error.response.body || error;
        }
        throw error;
    }
}

/**
 * Funcion para hacer una peticion put
 * @param path: string: path relativo de la peticion
 * @param body: dict: el body para el put
 * @param attachments: array: diccionario con estructura name, file, con el nombre y archivo que se desea enviar
 * @param params: dict: parametros para query string, opcionales
 * @return: instancia de superagent lista para ser recibida como promise
 * */
async function _putMultiPart(path, body, attachments, params = {}) {
    const url = makeUrl(path, params);
    const token = await getToken();
    if (!token) {
        throw new Error("No valid token available");
    }
    let result = request.put(url).set("Authorization", token);
    attachments &&
        attachments.forEach((attachment) => {
            result.attach(attachment.name, attachment.file);
        });
    const data = JSON.stringify(body);
    result.field("data", data);
    return result;
}

/**
 * Funcion para hacer una peticion put
 * @param path: string: path relativo de la peticion
 * @param body: dict: el body para el put
 * @param attachments: array: diccionario con estructura name, file, con el nombre y archivo que se desea enviar
 * @param params: dict: parametros para query string, opcionales
 * @return: Promise: promise del put
 * */
async function putAttachments(path, body, attachments, params = {}) {
    try {
        const response = await _putMultiPart(path, body, attachments, params);
        return response.body || response;
    } catch (error) {
        if (error.response) {
            errorHandler(error.response);
            throw error.response.body || error;
        }
        throw error;
    }
}

/**
 * Funcion para hacer una peticion put
 * @param path: string: path relativo de la peticion
 * @param body: dict: el body para el put
 * @param params: dict: parametros para query string, opcionales
 * @return: instancia de superagent lista para ser recibida como promise
 * */
async function _put(path, body, params = {}) {
    const url = makeUrl(path, params);
    const token = await getToken();

    if (!token) {
        throw new Error("No valid token available");
    }
    return request
        .put(url)
        .send(body)
        .set("Accept", "application/json")
        .set("Content-Type", "application/json")
        .set("Authorization", token); // Ya es seguro usarlo
}

/**
 * Funcion para hacer una peticion put
 * @param path: string: path relativo de la peticion
 * @param body: dict: el body para el put
 * @param params: dict: parametros para query string, opcionales
 * @return: Promise: promise del put
 * */
async function put(path, body, params = {}) {
    try {
        const response = await _put(path, body, params);
        return response.body || response;
    } catch (error) {
        // Si hay respuesta del servidor
        if (error.response) {
            errorHandler(error.response);
            throw error.response.body || error;
        }
        throw error;
    }
}

/**
 * Funcion para hacer una peticion delete
 * @param path: string: path relativo de la peticion
 * @return: instancia de superagent lista para ser recibida como promise
 * */
async function _delete(path) {
    const url = makeUrl(path);
    const token = await getToken();
    
    if (!token) {
        throw new Error("No valid token available");
    }
    return request
        .delete(url)
        .set("Accept", "application/json")
        .set("Content-Type", "application/json")
        .set("Authorization", token);
}

/**
 * Funcion para hacer una peticion delete
 * @param path: string: path relativo de la peticion
 * @return: Promise: promise del delete
 * */
async function eliminar(path) {
    try {
        const response = await _delete(path);
        return response.body || response;
    } catch (error) {
        if (error.response) {
            errorHandler(error.response);
            throw error.response.body || error;
        }
        throw error;
    }
}

/**
 * Funcion para hacer una peticion get
 * @param path: string: path relativo de la peticion
 * @param params: dict: parametros para query string, opcionales
 * @return: Promise: instancia de superagent lista para ser recibida como promise
 * */
async function _get(path, params = {}) {
    try {
        const url = makeUrl(path, params);
        const token = await getToken();

        if (!token) {
            throw new Error("No valid token available");
        }

        const response_get = await request
        .get(url)
        .set("Accept", "application/json")
        .set("Content-Type", "application/json")
        .set("Authorization", token);

        return response_get;
    } catch (error) {
        // Aquí capturamos errores de red, 401, 403, 500, etc.
        throw error; // Re-lanzamos para que el componente que llamó a _get sepa que falló
    }
}

/**
 * Funcion para hacer una peticion get
 * @param path: string: path relativo de la peticion
 * @param params: dict: parametros para query string, opcionales
 * @return: Promise: promise del get
 * */
async function get(path, params = {}) {
    try {
        const response = await _get(path, params);
        return response.body || response;
    } catch (error) {
        if (error.response) {
            errorHandler(error.response);
            throw error.response.body || error.response;
        }

        throw error;
    }
}

/**
 * Funcion para hacer una peticion get para PDF
 * @param path: string: path relativo de la peticion
 * @param params: dict: parametros para query string, opcionales
 * @return: Promise: instancia de superagent lista para ser recibida como promise
 * */
async function _getPdf(path, params = {}) {
    const url = makeUrl(path, params);
    const token = await getToken();

    if (!token) {
        throw new Error("No valid token available");
    }

    let reqPdf = request
        .get(url)
        .set("Accept", "application/json")
        .set("Content-Type", "application/pdf")
        .set("Authorization", token);

    return reqPdf;
}

/**
 * Funcion para hacer una peticion get para PDF
 * @param path: string: path relativo de la peticion
 * @param params: dict: parametros para query string, opcionales
 * @return: Promise: promise del get
 * */
async function getPdf(path, params = {}) {
    try {
        const response = await _getPdf(path, params);
        return response.body || response;
    } catch (error) {
        if (error.response) {
            errorHandler(error.response);
            throw error.response.body || error;
        }
        throw error;
    }
}

/**
 * Funcion para hacer una peticion get PDF
 * @param path: string: path relativo de la peticion
 * @param params: dict: parametros para query string, opcionales
 * @return: Promise: instancia de superagent lista para ser recibida como promise
 * */
async function _getPdf2(path, params = {}) {
    const url = makeUrl(path, params);
    const token = await getToken();
    let reqPdf2 = request.get(url).responseType('blob');             
    
    if (token) {
        reqPdf2 = reqPdf2.set('Authorization', token);
    }
    
    return reqPdf2;
    
}

/**
 * Funcion para hacer una peticion get
 * @param path: string: path relativo de la peticion
 * @param params: dict: parametros para query string, opcionales
 * @return: Promise: promise del get
 * */
async function getPdf2(path, params = {}) {
    try {
        const response = await _getPdf2(path, params);
        return response.body || response;
    } catch (error) {
        if (error.response) {
            errorHandler(error.response);
            throw error.response.body || error;
        }
        throw error;
    }
}

/**
 * Funcion para hacer una peticion get Excel
 * @param path: string: path relativo de la peticion
 * @param params: dict: parametros para query string, opcionales
 * @return: Promise: instancia de superagent lista para ser recibida como promise
 * */

async function _getExcel(path, params = {}) {
    const url = makeUrl(path, params);
    const token = await getToken();

    if (!token) {
        throw new Error("No valid token available");
    }

    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("GET", url, true);

        // Encabezados correctos para Excel
        xhr.setRequestHeader("Accept", "application/json");
        xhr.setRequestHeader(
        "Content-Type",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        );
        xhr.setRequestHeader("Authorization", token);

        xhr.responseType = "blob";

        xhr.onload = () => {
        if (xhr.status === 200) {
            resolve(xhr.response);
        } else {
            // Si falla (ej: 401, 403, 405), pasamos el objeto para que errorHandler actúe
            reject({
            status: xhr.status,
            response: { status: xhr.status, body: xhr.response },
            });
        }
        };

        xhr.onerror = () => {
        reject(new Error("Error de red al intentar descargar el Excel"));
        };

        xhr.send();
    });
}

async function getExcel(path, params = {}) {
    try {
        const response = await _getExcel(path, params);
        return response.body || response;
    } catch (error) {
        const errorResponse = error.response || (error.status ? error : null);

        if (errorResponse) {
            errorHandler(errorResponse);
        throw errorResponse.body || errorResponse;
        }
        throw error;
    }
}

/**
 * Se utiliza para Iniciar sesion ya que no hay un token existente
 */
async function _postLogin(path, body, params = {}) {
  const url = makeUrl(path, params);

  return request
    .post(url)
    .send(body)
    .set("Accept", "application/json")
    .set("Content-Type", "application/json");
}

/**
 * Función exclusiva para iniciar sesión
 * @param credentials: { username, password }
 */

async function login(path, body, params = {}) {
  try {
    const response = await _postLogin(path, body, params);
    return response.body || response;
  } catch (error) {
    if (error.response) {
      errorHandler(error.response);
      throw error.response.body || error;
    }
    throw error;
  }
}

/**
 * Se utiliza para Iniciar sesion ya que no hay un token existente
 */
async function _postLogin(path, body, params = {}) {
  const url = makeUrl(path, params);

  return request
    .post(url)
    .send(body)
    .set("Accept", "application/json")
    .set("Content-Type", "application/json");
}

/**
 * Función exclusiva para iniciar sesión
 * @param credentials: { username, password }
 */

async function login(path, body, params = {}) {
  try {
    const response = await _postLogin(path, body, params);
    return response.body || response;
  } catch (error) {
    if (error.response) {
      errorHandler(error.response);
      throw error.response.body || error;
    }
    throw error;
  }
}

/**
 * Verifica si el usuario existe por correo (sin token)
 */
async function _getVerifyUser(path, params = {}) {
  const url = makeUrl(path, params);

  return request
    .get(url)
    .set("Accept", "application/json")
    .set("Content-Type", "application/json");
}

/**
 * Verifica si el correo ingresado existe en el sistema
 * @param params: { email }
 */
async function verifyUser(path, params = {}) {
  try {
    const response = await _getVerifyUser(path, params);
    return response.body || response;
  } catch (error) {
    if (error.response) {
      errorHandler(error.response);
      throw error.response.body || error;
    }
    throw error;
  }
}

/**
 * Se utiliza para cambio de password con token de reseteo (sin token de sesión)
 */
async function _putChangePassword(path, body, params = {}) {
  const url = makeUrl(path, params);

  return request
    .put(url)
    .send(body)
    .set("Accept", "application/json")
    .set("Content-Type", "application/json");
}

/**
 * Cambia el password del usuario usando el token de reseteo
 * @param body: { token, new_password }
 */
async function changePassword(path, body, params = {}) {
  try {
    const response = await _putChangePassword(path, body, params);
    return response.body || response;
  } catch (error) {
    if (error.response) {
      errorHandler(error.response);
      throw error.response.body || error;
    }
    throw error;
  }
}

export const api = { 
    get, 
    post, 
    put, 
    eliminar, 
    postAttachments, 
    putAttachments, 
    getPdf, 
    getExcel, 
    getPdf2, 
    login,
    verifyUser,
    changePassword
};

